import { classNames, twMerge } from '@panwds/tailwindcss';
import React, { Children, cloneElement, useEffect, useState } from 'react';
import { isReactComponent } from '../../utils/index.js';
import { MULTILINE_PLACEMENTS, POPOVER_PLACEMENTS, SINGLELINE_PLACEMENTS } from '../../utils/constants.js';
import { usePopperReuse as usePopper } from '../../utils/usePopperReuse.js';
import { Portal } from '../Portal/index.js';
export function Tooltip({ children, dataMetrics, dataTestId, label, singleLine = false, addClassName, containerClassName, defaultPlacement = POPOVER_PLACEMENTS.BOTTOM_END }) {
    const child = Children.only(children);
    const [placement, setPlacement] = useState(defaultPlacement);
    const [isHover, setIsHover] = useState(false);
    const singleLinePlacements = Object.values(SINGLELINE_PLACEMENTS);
    const multiLinePlacements = Object.values(MULTILINE_PLACEMENTS);
    const caretTooltipOffset = 17;
    const { triggerRef, containerRef, rawContainerRef, popperInstanceRef } = usePopper({
        placement: defaultPlacement,
        strategy: 'fixed',
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: ({ placement, reference }) => {
                        switch (placement) {
                            case POPOVER_PLACEMENTS.BOTTOM_START:
                            case POPOVER_PLACEMENTS.TOP_START:
                                return [reference.width / 2 - caretTooltipOffset, 0];
                            case POPOVER_PLACEMENTS.BOTTOM_END:
                            case POPOVER_PLACEMENTS.TOP_END:
                                return [-(reference.width / 2 - caretTooltipOffset), 0];
                            case POPOVER_PLACEMENTS.LEFT_START:
                            case POPOVER_PLACEMENTS.RIGHT_START:
                                return [reference.height / 2 - caretTooltipOffset, 0];
                            case POPOVER_PLACEMENTS.LEFT_END:
                            case POPOVER_PLACEMENTS.RIGHT_END:
                                return [-(reference.height / 2 - caretTooltipOffset), 0];
                            default:
                                return [];
                        }
                    }
                }
            },
            {
                name: 'getPlacement',
                enabled: true,
                phase: 'afterWrite',
                fn({ state }) {
                    setPlacement(state.placement);
                }
            },
            {
                name: 'flip',
                options: {
                    fallbackPlacements: singleLine ? singleLinePlacements : multiLinePlacements
                }
            }
        ]
    });
    /**
     * Performance
     *
     * Once we create a popper with createPopper, it stays "alive".
     * This means while scrolling the page, the popper is constantly being updated, even if it is not visible.
     * We can disable the event listeners when the tooltip is hidden to optimize it:
     */
    const show = () => {
        // Make the tooltip visible
        rawContainerRef.current?.classList.remove('tw-hidden');
        // Enable the event listeners
        popperInstanceRef.current?.setOptions((options) => ({
            ...options,
            modifiers: [
                ...options.modifiers.filter((modifier) => {
                    return modifier.name !== 'eventListeners';
                }),
                { name: 'eventListeners', enabled: true }
            ]
        }));
        // Update its position
        popperInstanceRef.current?.update();
    };
    const hide = () => {
        // Hide the tooltip
        rawContainerRef.current?.classList.add('tw-hidden');
        // Disable the event listeners
        popperInstanceRef.current?.setOptions((options) => {
            return {
                ...options,
                modifiers: [
                    ...options.modifiers.filter((modifier) => {
                        return modifier.name !== 'eventListeners';
                    }),
                    { name: 'eventListeners', enabled: false }
                ]
            };
        });
    };
    useEffect(() => {
        popperInstanceRef.current?.setOptions((options) => {
            return {
                ...{ ...options, placement: defaultPlacement },
                modifiers: [
                    ...options.modifiers.filter((modifier) => {
                        return modifier.name !== 'eventListeners';
                    }),
                    { name: 'eventListeners', enabled: false }
                ]
            };
        });
        return () => {
            popperInstanceRef.current?.destroy();
        };
    }, [defaultPlacement]); // only useful for storyboard use case
    let defaultStyle = "tw-relative tw-z-50 before:tw-content-[''] before:tw-absolute before:tw-h-0 before:tw-w-0 before:tw-border-solid before:tw-border-transparent before:tw-border-8 after:tw-content-[''] after:tw-absolute after:tw-h-0 after:tw-w-0 after:tw-border-solid after:tw-border-transparent after:tw-border-8";
    if (placement === POPOVER_PLACEMENTS.BOTTOM_END) {
        defaultStyle = classNames(defaultStyle, 'tw-top-3 before:tw-right-2 before:-tw-top-2 before:tw-border-t-0 before:tw-border-b-gray-800 after:tw-right-2 after:tw-border-t-0 after:-tw-top-1.5', 'after:tw-border-b-gray-900');
    }
    else if (placement === POPOVER_PLACEMENTS.BOTTOM_START) {
        defaultStyle = classNames(defaultStyle, 'tw-top-3 before:tw-left-2 before:-tw-top-2 before:tw-border-t-0 before:tw-border-b-gray-800 after:tw-left-2 after:tw-border-t-0 after:-tw-top-1.5', 'after:tw-border-b-gray-900');
    }
    else if (placement === POPOVER_PLACEMENTS.TOP_START) {
        defaultStyle = classNames(defaultStyle, 'tw-bottom-3 before:tw-left-2 before:-tw-bottom-2 before:tw-border-b-0 before:tw-border-t-gray-800 after:tw-left-2 after:tw-border-b-0 after:-tw-bottom-1.5 ', 'after:tw-border-t-gray-900');
    }
    else if (placement === POPOVER_PLACEMENTS.TOP_END) {
        defaultStyle = classNames(defaultStyle, 'tw-bottom-3 before:tw-right-2 before:-tw-bottom-2 before:tw-border-b-0 before:tw-border-t-gray-800 after:tw-right-2 after:tw-border-b-0 after:-tw-bottom-1.5', 'after:tw-border-t-gray-900');
    }
    else if (placement === POPOVER_PLACEMENTS.LEFT_START) {
        defaultStyle = classNames(defaultStyle, 'tw-right-3 before:tw-top-2 before:-tw-right-2 before:tw-border-r-0 before:tw-border-l-gray-800 after:tw-top-2 after:tw-border-r-0 after:-tw-right-1.5', 'after:tw-border-l-gray-900');
    }
    else if (placement === POPOVER_PLACEMENTS.LEFT_END) {
        defaultStyle = classNames(defaultStyle, 'tw-right-3 before:tw-bottom-2 before:-tw-right-2 before:tw-border-r-0 before:tw-border-l-gray-800 after:tw-bottom-2 after:tw-border-r-0 after:-tw-right-1.5', 'after:tw-border-l-gray-900');
    }
    else if (placement === POPOVER_PLACEMENTS.RIGHT_START) {
        defaultStyle = classNames(defaultStyle, 'tw-left-3 before:tw-top-2 before:-tw-left-2 before:tw-border-l-0 before:tw-border-r-gray-800 after:tw-top-2 after:tw-border-l-0 after:-tw-left-1.5', 'after:tw-border-r-gray-900');
    }
    else if (placement === POPOVER_PLACEMENTS.RIGHT_END) {
        defaultStyle = classNames(defaultStyle, 'tw-left-3 before:tw-bottom-2 before:-tw-left-2 before:tw-border-l-0 before:tw-border-r-gray-800 after:tw-bottom-2 after:tw-border-l-0 after:-tw-left-1.5', 'after:tw-border-r-gray-900');
    }
    else if (placement === POPOVER_PLACEMENTS.BOTTOM) {
        defaultStyle = classNames(defaultStyle, 'tw-top-3 before:tw-right-1/2 before:tw-translate-x-1/2 before:-tw-top-2 before:tw-border-t-0 before:tw-border-b-gray-800 after:tw-right-1/2 after:tw-translate-x-1/2 after:tw-border-t-0 after:-tw-top-1.5 ', 'after:tw-border-b-gray-900');
    }
    else if (placement === POPOVER_PLACEMENTS.TOP) {
        defaultStyle = classNames(defaultStyle, 'tw-bottom-3 before:tw-right-1/2 before:tw-translate-x-1/2 before:-tw-bottom-2 before:tw-border-b-0 before:tw-border-t-gray-800 after:tw-right-1/2 after:tw-translate-x-1/2 after:tw-border-b-0 after:-tw-bottom-1.5 ', 'after:tw-border-t-gray-900');
    }
    else if (placement === POPOVER_PLACEMENTS.RIGHT) {
        defaultStyle = classNames(defaultStyle, 'tw-left-3 before:tw-bottom-1/2 before:tw-translate-y-1/2 before:-tw-left-2 before:tw-border-l-0 before:tw-border-r-gray-800 after:tw-bottom-1/2 after:tw-translate-y-1/2 after:tw-border-l-0 after:-tw-left-1.5 ', 'after:tw-border-r-gray-900');
    }
    else if (placement === POPOVER_PLACEMENTS.LEFT) {
        defaultStyle = classNames(defaultStyle, 'tw-right-3 before:tw-bottom-1/2 before:tw-translate-y-1/2 before:-tw-right-2 before:tw-border-r-0 before:tw-border-l-gray-800 after:tw-bottom-1/2 after:tw-translate-y-1/2 after:tw-border-r-0 after:-tw-right-1.5 ', 'after:tw-border-l-gray-900');
    }
    const className = classNames(addClassName, 'tw-bg-gray-900 tw-text-dark-bg tw-border tw-border-gray-800', 'tw-font-lato tw-text-xs tw-p-2 tw-rounded tw-shadow-popover dark:tw-shadow-dark-popover tw-border-solid tw-max-w-[230px]', !singleLine && 'tw-break-words', defaultStyle);
    return (React.createElement(React.Fragment, null,
        isReactComponent(child.type) ? (React.createElement("div", { ref: triggerRef, className: "tw-flex tw-items-center" }, cloneElement(child, {
            onMouseEnter: () => {
                setIsHover(true);
                show();
            },
            onMouseLeave: () => {
                setIsHover(false);
                hide();
            },
            onFocus: () => {
                setIsHover(true);
                show();
            },
            onBlur: () => {
                setIsHover(false);
                hide();
            }
        }))) : (cloneElement(child, {
            onMouseEnter: () => {
                setIsHover(true);
                show();
            },
            onMouseLeave: () => {
                setIsHover(false);
                hide();
            },
            onFocus: () => {
                setIsHover(true);
                show();
            },
            onBlur: () => {
                setIsHover(false);
                hide();
            },
            ref: triggerRef
        })),
        React.createElement(Portal, null,
            React.createElement("div", { "data-metrics": dataMetrics, "data-testid": dataTestId || 'tooltip', ref: containerRef, role: "tooltip", className: twMerge('tw-z-50 tw-group tw-hidden', containerClassName) }, isHover && React.createElement("div", { className: className }, label)))));
}
//# sourceMappingURL=Tooltip.js.map